import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { userAPI } from '../../api';

// 스타일 컴포넌트
const PageContainer = styled.div`
  /* max-width: 800px; */
  /* margin: 0 auto; */
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
  color: #333;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 1rem;
`;

const RankingContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const RankingHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 120px;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
  color: #495057;
  font-weight: 600;
  border-bottom: 1px solid #e9ecef;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const RankingList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const RankingItem = styled.li<{ isTop3: boolean }>`
  display: grid;
  grid-template-columns: 80px 1fr 120px;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  ${props => props.isTop3 && `
    background-color: ${props.isTop3 ? '#fff9e6' : 'transparent'};
    
    &:hover {
      background-color: ${props.isTop3 ? '#fff4cc' : '#f8f9fa'};
    }
  `}

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const RankNumber = styled.div<{ rank: number }>`
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  
  ${props => {
    if (props.rank === 1) return 'color: #f59f00;';
    if (props.rank === 2) return 'color: #74b816;';
    if (props.rank === 3) return 'color: #4263eb;';
    return 'color: #495057;';
  }}
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-right: 0.5rem;
  
  ${props => {
    if (props.rank === 1) return 'background-color: #ffec99; color: #e67700;';
    if (props.rank === 2) return 'background-color: #d3f9d8; color: #2b8a3e;';
    if (props.rank === 3) return 'background-color: #dbe4ff; color: #3b5bdb;';
    return 'background-color: #f1f3f5; color: #495057;';
  }}
`;

const Username = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #212529;
`;

const Points = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-weight: 600;
  color: #e03131;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem 0;
  color: #868e96;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 2rem;
  color: #e03131;
  border: 1px solid #ffc9c9;
  border-radius: 8px;
  background-color: #fff5f5;
  margin-top: 2rem;
`;

const RefreshButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #4263eb;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  margin-top: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #3b5bdb;
  }
`;

interface RankUser {
  id: number;
  username: string;
  nickname: string;
  points: number;
}

const Ranking: React.FC = () => {
  const [userRanking, setUserRanking] = useState<RankUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRanking = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userAPI.getPointRanking(20);
      if (response.data.success) {
        setUserRanking(response.data.data);
      } else {
        setError(response.data.message || '랭킹 데이터를 불러오는 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error('랭킹 데이터 로딩 오류:', err);
      setError('랭킹 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRanking();
  }, []);

  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  return (
    <PageContainer>
      <PageTitle>포인트 랭킹</PageTitle>
      
      {loading ? (
        <LoadingContainer>
          <p>랭킹 데이터를 불러오는 중입니다...</p>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <p>{error}</p>
          <RefreshButton onClick={fetchRanking}>다시 시도</RefreshButton>
        </ErrorContainer>
      ) : (
        <RankingContainer>
          <RankingHeader>
            <div>순위</div>
            <div>닉네임</div>
            <div>포인트</div>
          </RankingHeader>
          
          <RankingList>
            {userRanking.map((user, index) => (
              <RankingItem key={user.id} isTop3={index < 3}>
                <RankNumber rank={index + 1}>
                  <RankBadge rank={index + 1}>{index + 1}</RankBadge>
                </RankNumber>
                <Username>{user.nickname || user.username}</Username>
                <Points>{formatNumber(user.points)} P</Points>
              </RankingItem>
            ))}
            
            {userRanking.length === 0 && (
              <RankingItem isTop3={false}>
                <div style={{ gridColumn: '1 / span 3', textAlign: 'center', padding: '2rem 0', color: '#868e96' }}>
                  랭킹 데이터가 없습니다.
                </div>
              </RankingItem>
            )}
          </RankingList>
        </RankingContainer>
      )}
    </PageContainer>
  );
};

export default Ranking; 